import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Plus, Eye, EyeOff, Trash2, Pencil, Check, X } from 'lucide-react';
import ExpertNoteByline from './ExpertNoteByline';
import {
  adminListLandscapingQuestions,
  adminAddExpertNote,
  adminPatchExpertNote,
  adminDeleteExpertNote,
  adminPatchQuestion,
  adminDeleteQuestion,
} from '../services/landscapingQuestionsApi';

export default function AdminLandscapingQA() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [newNote, setNewNote] = useState({});
  const [saving, setSaving] = useState({});
  // Edit question state: { [id]: { question_text, ai_answer, editing, saving } }
  const [editQ, setEditQ] = useState({});
  /** Expert note inline edit: key `${qId}::${noteId}` → { advice_text, contributor_label } */
  const [editNote, setEditNote] = useState({});

  const noteKey = (qId, noteId) => `${qId}::${noteId}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await adminListLandscapingQuestions(80);
      setItems(list);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAddNote = async (questionId) => {
    const text = (newNote[questionId]?.advice_text || '').trim();
    if (!text) return;
    setSaving((s) => ({ ...s, [questionId]: true }));
    try {
      await adminAddExpertNote(questionId, {
        advice_text: text,
        contributor_label: (newNote[questionId]?.contributor_label || '').trim() || undefined,
        published: newNote[questionId]?.published !== false,
      });
      setNewNote((n) => ({ ...n, [questionId]: { advice_text: '', contributor_label: '', published: true } }));
      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving((s) => ({ ...s, [questionId]: false }));
    }
  };

  const togglePublish = async (qId, note) => {
    try {
      await adminPatchExpertNote(qId, note.id, { published: !note.published });
      await load();
    } catch (err) { alert(err.message); }
  };

  const removeNote = async (qId, noteId) => {
    if (!window.confirm('Delete this expert note?')) return;
    try {
      await adminDeleteExpertNote(qId, noteId);
      await load();
    } catch (err) { alert(err.message); }
  };

  const startEdit = (row) => {
    setEditQ((e) => ({
      ...e,
      [row.id]: { question_text: row.question_text, ai_answer: row.ai_answer || '', editing: true, saving: false },
    }));
  };

  const cancelEdit = (id) => setEditQ((e) => ({ ...e, [id]: { ...e[id], editing: false } }));

  const saveEdit = async (id) => {
    const ed = editQ[id];
    if (!ed) return;
    setEditQ((e) => ({ ...e, [id]: { ...e[id], saving: true } }));
    try {
      await adminPatchQuestion(id, { question_text: ed.question_text, ai_answer: ed.ai_answer });
      setEditQ((e) => ({ ...e, [id]: { ...e[id], editing: false, saving: false } }));
      await load();
    } catch (err) {
      alert(err.message);
      setEditQ((e) => ({ ...e, [id]: { ...e[id], saving: false } }));
    }
  };

  const deleteQuestion = async (id, questionText) => {
    if (!window.confirm(`Delete this question and all its notes?\n\n"${questionText.slice(0, 120)}…"\n\nThis cannot be undone.`)) return;
    try {
      await adminDeleteQuestion(id);
      if (expandedId === id) setExpandedId(null);
      await load();
    } catch (err) { alert(err.message); }
  };

  const startEditNote = (qId, note) => {
    setEditNote((e) => ({
      ...e,
      [noteKey(qId, note.id)]: {
        advice_text: note.advice_text || '',
        contributor_label: note.contributor_label || '',
      },
    }));
  };

  const cancelEditNote = (qId, noteId) => {
    const k = noteKey(qId, noteId);
    setEditNote((e) => {
      const next = { ...e };
      delete next[k];
      return next;
    });
  };

  const saveEditNote = async (qId, noteId) => {
    const k = noteKey(qId, noteId);
    const draft = editNote[k];
    if (!draft) return;
    const text = (draft.advice_text || '').trim();
    if (!text) {
      alert('Response text cannot be empty.');
      return;
    }
    setSaving((s) => ({ ...s, [k]: true }));
    try {
      await adminPatchExpertNote(qId, noteId, {
        advice_text: text,
        contributor_label: (draft.contributor_label || '').trim() || null,
      });
      setEditNote((e) => {
        const next = { ...e };
        delete next[k];
        return next;
      });
      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving((s) => ({ ...s, [k]: false }));
    }
  };

  const pendingNotes = useMemo(() => {
    const out = [];
    for (const row of items) {
      for (const note of row.expert_notes || []) {
        if (!note.published) {
          out.push({
            ...note,
            questionId: row.id,
            questionText: row.question_text,
          });
        }
      }
    }
    return out.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [items]);

  const approveNote = async (qId, note) => {
    try {
      await adminPatchExpertNote(qId, note.id, { published: true });
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading && !items.length) {
    return (
      <div className="flex items-center gap-2 text-gray-600">
        <RefreshCw className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Yard Q&amp;A (AI + expert notes)</h3>
        <p className="text-sm text-gray-600 mt-1">
          Edit the question or AI answer, add or edit expert responses (changes apply everywhere they appear), publish or
          unpublish notes, or delete individual notes or whole questions.
        </p>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">{error}</div>}

      {pendingNotes.length > 0 && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3">
          <h4 className="font-semibold text-amber-900 flex items-center gap-2">
            Pending designer notes ({pendingNotes.length})
          </h4>
          <p className="text-sm text-amber-900/90">
            Approve to show on the public Dear John column and /ask pages. Delete to discard.
          </p>
          <ul className="space-y-3">
            {pendingNotes.map((note) => {
              const pk = noteKey(note.questionId, note.id);
              const draft = editNote[pk];
              const savingNote = saving[pk];
              return (
                <li
                  key={`${note.questionId}-${note.id}`}
                  className="bg-white border border-amber-200 rounded-lg p-3 text-sm"
                >
                  <p className="text-xs text-gray-500 font-mono truncate mb-1">{note.questionId}</p>
                  <p className="text-gray-700 line-clamp-2 mb-2">{note.questionText}</p>
                  {draft ? (
                    <div className="border-t border-gray-100 pt-2 space-y-2">
                      <label className="block text-xs font-semibold text-gray-500">Response text</label>
                      <textarea
                        rows={4}
                        value={draft.advice_text}
                        onChange={(e) =>
                          setEditNote((en) => ({
                            ...en,
                            [pk]: { ...en[pk], advice_text: e.target.value },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <label className="block text-xs font-semibold text-gray-500">Contributor label</label>
                      <input
                        type="text"
                        value={draft.contributor_label}
                        onChange={(e) =>
                          setEditNote((en) => ({
                            ...en,
                            [pk]: { ...en[pk], contributor_label: e.target.value },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={savingNote}
                          onClick={() => saveEditNote(note.questionId, note.id)}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 disabled:opacity-50"
                        >
                          {savingNote ? 'Saving…' : 'Save response'}
                        </button>
                        <button
                          type="button"
                          onClick={() => cancelEditNote(note.questionId, note.id)}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-gray-900 whitespace-pre-wrap border-t border-gray-100 pt-2">{note.advice_text}</p>
                      {note.contributor_label && (
                        <p className="text-xs text-blue-800 mt-1">
                          By: <ExpertNoteByline note={note} className="inline text-xs text-blue-800" inline />
                        </p>
                      )}
                    </>
                  )}
                  {note.created_by_admin_email && (
                    <p className="text-xs text-gray-500 mt-0.5">{note.created_by_admin_email}</p>
                  )}
                  {!draft && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => approveNote(note.questionId, note)}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700"
                      >
                        Approve & publish
                      </button>
                      <button
                        type="button"
                        onClick={() => startEditNote(note.questionId, note)}
                        className="px-3 py-1.5 border border-blue-200 text-blue-800 rounded-lg text-xs font-semibold hover:bg-blue-50 flex items-center gap-1"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit response
                      </button>
                      <button
                        type="button"
                        onClick={() => removeNote(note.questionId, note.id)}
                        className="px-3 py-1.5 border border-red-200 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-50"
                      >
                        Delete note
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => load()}
        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
      >
        <RefreshCw className="w-4 h-4" /> Refresh
      </button>

      <div className="space-y-4">
        {items.length === 0 && !loading && (
          <p className="text-sm text-gray-500">No questions yet. They appear when visitors submit from the public page.</p>
        )}
        {items.map((row) => {
          const isOpen = expandedId === row.id;
          const ed = editQ[row.id] || {};
          return (
            <div key={row.id} className="border border-gray-200 rounded-lg bg-white overflow-hidden">

              {/* Header row */}
              <div className="flex items-start gap-2 px-4 py-3 hover:bg-gray-50">
                <button
                  type="button"
                  onClick={() => setExpandedId(isOpen ? null : row.id)}
                  className="flex-1 text-left min-w-0"
                >
                  <p className="text-xs text-gray-400 font-mono truncate">{row.id}</p>
                  <p className="text-sm text-gray-900 mt-1 line-clamp-2">{row.question_text}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {row.status} · {row.expert_notes?.length || 0} note(s)
                  </p>
                </button>
                {/* Per-row actions */}
                <div className="flex items-center gap-1 flex-shrink-0 mt-1">
                  <button
                    type="button"
                    onClick={() => { setExpandedId(row.id); startEdit(row); }}
                    className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                    title="Edit question"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteQuestion(row.id, row.question_text)}
                    className="p-1.5 rounded hover:bg-red-50 text-red-500"
                    title="Delete question"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-400 ml-1">{isOpen ? '▼' : '▶'}</span>
                </div>
              </div>

              {/* Expanded body */}
              {isOpen && (
                <div className="px-4 pb-5 border-t border-gray-100 space-y-4">

                  {/* Edit mode */}
                  {ed.editing ? (
                    <div className="space-y-3 pt-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Question text</label>
                        <textarea
                          rows={3}
                          value={ed.question_text}
                          onChange={(e) => setEditQ((q) => ({ ...q, [row.id]: { ...q[row.id], question_text: e.target.value } }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">AI answer</label>
                        <textarea
                          rows={6}
                          value={ed.ai_answer}
                          onChange={(e) => setEditQ((q) => ({ ...q, [row.id]: { ...q[row.id], ai_answer: e.target.value } }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={ed.saving}
                          onClick={() => saveEdit(row.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                        >
                          <Check className="w-4 h-4" /> {ed.saving ? 'Saving…' : 'Save changes'}
                        </button>
                        <button
                          type="button"
                          onClick={() => cancelEdit(row.id)}
                          className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <X className="w-4 h-4" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="pt-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">AI answer</p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap bg-emerald-50/50 border border-emerald-100 rounded p-3">
                        {row.ai_answer || '—'}
                      </p>
                    </div>
                  )}

                  <p className="text-xs text-gray-500">
                    Public link:{' '}
                    <a href={`/ask/${row.id}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 break-all">
                      {typeof window !== 'undefined' ? `${window.location.origin}/ask/${row.id}` : `/ask/${row.id}`}
                    </a>
                  </p>

                  {/* Existing notes */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Expert notes</p>
                    {(row.expert_notes || []).length === 0 && (
                      <p className="text-sm text-gray-500 italic">No expert notes yet.</p>
                    )}
                    {(row.expert_notes || []).map((note) => {
                      const nk = noteKey(row.id, note.id);
                      const draft = editNote[nk];
                      const savingNote = saving[nk];
                      return (
                        <div key={note.id} className="mb-3 p-3 border border-blue-100 rounded-lg bg-blue-50/50 text-sm">
                          {draft ? (
                            <div className="space-y-2">
                              <label className="block text-xs font-semibold text-gray-500">Response text</label>
                              <textarea
                                rows={5}
                                value={draft.advice_text}
                                onChange={(e) =>
                                  setEditNote((en) => ({
                                    ...en,
                                    [nk]: { ...en[nk], advice_text: e.target.value },
                                  }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                              <label className="block text-xs font-semibold text-gray-500">Contributor label</label>
                              <input
                                type="text"
                                value={draft.contributor_label}
                                onChange={(e) =>
                                  setEditNote((en) => ({
                                    ...en,
                                    [nk]: { ...en[nk], contributor_label: e.target.value },
                                  }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                              <div className="flex flex-wrap gap-2 pt-1">
                                <button
                                  type="button"
                                  disabled={savingNote}
                                  onClick={() => saveEditNote(row.id, note.id)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 disabled:opacity-50"
                                >
                                  <Check className="w-3.5 h-3.5" /> {savingNote ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => cancelEditNote(row.id, note.id)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 hover:bg-gray-50"
                                >
                                  <X className="w-3.5 h-3.5" /> Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex justify-between items-start gap-2">
                                <p className="text-gray-900 whitespace-pre-wrap flex-1">{note.advice_text}</p>
                                <div className="flex gap-1 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => startEditNote(row.id, note)}
                                    className="p-1.5 rounded hover:bg-blue-100 text-blue-700"
                                    title="Edit response"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => togglePublish(row.id, note)}
                                    className="p-1.5 rounded hover:bg-blue-100"
                                    title={note.published ? 'Unpublish' : 'Publish'}
                                  >
                                    {note.published
                                      ? <Eye className="w-4 h-4 text-green-700" />
                                      : <EyeOff className="w-4 h-4 text-gray-500" />}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeNote(row.id, note.id)}
                                    className="p-1.5 rounded hover:bg-red-50 text-red-600"
                                    title="Delete note"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              {note.contributor_label && (
                                <p className="text-xs text-blue-800 mt-1">
                                  By: <ExpertNoteByline note={note} className="inline text-xs text-blue-800" inline />
                                </p>
                              )}
                              <p className="text-xs text-gray-500 mt-1">{note.published ? 'Published' : 'Draft'}</p>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Add new note */}
                  <div className="border-t border-gray-100 pt-3">
                    <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                      <Plus className="w-4 h-4" /> Add expert note
                    </p>
                    <textarea
                      placeholder="Short expert advice (shown below AI answer when published)"
                      value={newNote[row.id]?.advice_text || ''}
                      onChange={(e) =>
                        setNewNote((n) => ({ ...n, [row.id]: { ...n[row.id], advice_text: e.target.value } }))
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Contributor label (optional), e.g. John Connors, PlantADay"
                      value={newNote[row.id]?.contributor_label || ''}
                      onChange={(e) =>
                        setNewNote((n) => ({ ...n, [row.id]: { ...n[row.id], contributor_label: e.target.value } }))
                      }
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={newNote[row.id]?.published !== false}
                        onChange={(e) =>
                          setNewNote((n) => ({ ...n, [row.id]: { ...n[row.id], published: e.target.checked } }))
                        }
                      />
                      Published (visible on public page)
                    </label>
                    <button
                      type="button"
                      disabled={saving[row.id] || !(newNote[row.id]?.advice_text || '').trim()}
                      onClick={() => handleAddNote(row.id)}
                      className="mt-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:bg-gray-400"
                    >
                      {saving[row.id] ? 'Saving…' : 'Save expert note'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
